import {
  api,
  ApiError,
  type AdminUserSummary,
  type AuditEvent,
  type DiskUsageResponse,
  type RecoveryUser,
  type ReplyBacklogEntry,
  type StorageAnalyticsResponse,
  type StorageContributor,
  type StorageInfoShareOwner,
  type StorageSedrRoomItem,
  type StorageSharedFolderItem,
  type SystemInfoResponse,
  type PasswordPolicy,
  type SystemSettingsResponse,
} from '../api/client';
import { getAppState } from '../app/index';
import {
  buildAdminUrl,
  type AdminSection,
  parseAdminUrl,
  readAdminUrlState,
} from '../app/navigationState';
import { clearPasswordPoliciesCache } from '../crypto/passwordPoliciesApi';
import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { mountSectionNav } from '../ui/sectionNav';
import { showConfirmDialog } from '../ui/confirmDialog';
import { createHelpTip } from '../help/helpTips';
import {
  clonePasswordPolicies,
  mountPasswordPoliciesEditor,
  validatePasswordPoliciesDraft,
} from './adminPasswordPolicies';
import {
  downloadActorsCsv,
  downloadContributorsCsv,
  downloadInfoShareOwnersCsv,
  downloadModulesCsv,
  downloadSedrRoomsCsv,
  downloadSharedFoldersCsv,
  formatStorageBytes,
  renderAnalyticsCharts,
  renderInfoShareOwnersTable,
  renderSedrRoomsTable,
  renderSharedFoldersTable,
  renderStorageTableSection,
  createCsvButton,
} from './adminDiskAnalytics';
import {
  AUDIT_CSV_CAP,
  AUDIT_DOM_CAP,
  AUDIT_EVENT_GROUPS,
  AUDIT_LIST_PAGE_SIZE,
  AUDIT_LOAD_ALL_PAGE_SIZE,
  AUDIT_MODULES,
  auditCsvFileName,
  auditEventBadgeClass,
  auditFiltersActive,
  buildAuditCsv,
  downloadTextFile,
  emptyAuditFilters,
  eventTypesForModule,
  formatAuditSummary,
  formatAuditTooltip,
  readAuditFiltersFromUrl,
  tAuditEventType,
  toAuditQuery,
  writeAuditFiltersToUrl,
  type AuditFilters,
  type AuditModule,
} from './adminAudit';
import {
  applySettingsFieldErrors,
  clearSettingsFieldErrors,
  validateSettingsFormClient,
} from './adminSettingsValidation';
import { bindPhoneInputNormalize, normalizeToE164 } from '../crypto/smsPassword';

function renderAuditRow(event: AuditEvent): HTMLElement {
  const row = document.createElement('article');
  row.className = 'audit-row';
  row.dataset.eventId = event.id;
  row.innerHTML = `
    <div class="audit-row__meta">
      <button type="button" class="audit-row__type"></button>
      <span class="muted audit-row__time"></span>
    </div>
    <div class="audit-row__body muted"></div>
  `;

  const typeEl = row.querySelector('.audit-row__type') as HTMLButtonElement;
  const timeEl = row.querySelector('.audit-row__time') as HTMLSpanElement;
  const bodyEl = row.querySelector('.audit-row__body') as HTMLDivElement;

  typeEl.className = auditEventBadgeClass(event.eventType);
  typeEl.textContent = tAuditEventType(event.eventType);
  timeEl.textContent = new Date(event.createdAt).toLocaleString();
  bodyEl.textContent = formatAuditSummary(event);
  const tooltip = formatAuditTooltip(event);
  if (tooltip) {
    row.title = tooltip;
  }
  typeEl.addEventListener('click', () => {
    row.dispatchEvent(new CustomEvent('audit-filter-type', { bubbles: true, detail: event.eventType }));
  });

  return row;
}

function formatBytes(bytes: number): string {
  return formatStorageBytes(bytes);
}

function renderDiskUsage(usage: DiskUsageResponse): string {
  const lines = [
    `${t('admin.disk.total')}: ${formatBytes(usage.totalBytes)}`,
    `${t('admin.disk.blobs')}: ${formatBytes(usage.blobBytes)}`,
    `${t('admin.disk.database')}: ${formatBytes(usage.databaseBytes)}`,
    `${t('admin.disk.warningThreshold')}: ${formatBytes(usage.deploymentWarningBytes)}`,
  ];

  if (usage.warningThresholdReached) {
    lines.push(t('admin.disk.warningReached'));
  }

  return lines.join('\n');
}

function formatOptional(value: string | null | undefined): string {
  return value && value.trim().length > 0 ? value : t('admin.system.value.none');
}

function formatYesNo(value: boolean): string {
  return value ? t('admin.system.value.yes') : t('admin.system.value.no');
}

function systemInfoRow(labelKey: string, value: string, options?: { breakLong?: boolean }): string {
  const rowClass = options?.breakLong ? 'admin-system-row admin-system-row--break' : 'admin-system-row';
  return `
    <div class="${rowClass}">
      <dt data-i18n="${labelKey}"></dt>
      <dd>${escapeHtml(value)}</dd>
    </div>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function renderSystemInfo(info: SystemInfoResponse): string {
  const started = new Date(info.processStartedAtUtc).toLocaleString();
  const lastReportedAt = info.lastReportedAt
    ? new Date(info.lastReportedAt).toLocaleString()
    : t('admin.system.value.none');

  return `
    <div class="admin-system-grid">
      <section class="admin-system-group">
        <h3 data-i18n="admin.system.group.application"></h3>
        <dl class="admin-system-list">
          ${systemInfoRow('admin.system.version', info.version)}
          ${systemInfoRow('admin.system.environment', info.environment)}
          ${systemInfoRow('admin.system.framework', info.frameworkDescription)}
          ${systemInfoRow('admin.system.processStarted', started)}
        </dl>
      </section>
      <section class="admin-system-group">
        <h3 data-i18n="admin.system.group.host"></h3>
        <dl class="admin-system-list">
          ${systemInfoRow('admin.system.hostname', info.hostname)}
          ${systemInfoRow('admin.system.os', info.osDescription)}
          ${systemInfoRow('admin.system.osArchitecture', info.osArchitecture)}
          ${systemInfoRow('admin.system.processArchitecture', info.processArchitecture)}
        </dl>
      </section>
      <section class="admin-system-group">
        <h3 data-i18n="admin.system.group.storage"></h3>
        <dl class="admin-system-list">
          ${systemInfoRow('admin.system.databasePath', info.databasePath, { breakLong: true })}
          ${systemInfoRow('admin.system.blobStoragePath', info.blobStoragePath, { breakLong: true })}
        </dl>
      </section>
      <section class="admin-system-group">
        <h3 data-i18n="admin.system.group.versionReporting"></h3>
        <dl class="admin-system-list">
          ${systemInfoRow('admin.system.versionReporting.enabled', formatYesNo(info.versionReportingEnabled))}
          ${systemInfoRow('admin.system.versionReporting.environment', formatOptional(info.versionReportingEnvironment))}
          ${systemInfoRow('admin.system.versionReporting.proxy', formatYesNo(info.versionReportingProxyConfigured))}
          ${systemInfoRow('admin.system.versionReporting.lastVersion', formatOptional(info.lastReportedVersion))}
          ${systemInfoRow('admin.system.versionReporting.lastAt', lastReportedAt)}
        </dl>
      </section>
    </div>
  `;
}

function renderReplyBacklogRow(entry: ReplyBacklogEntry): HTMLElement {
  const row = document.createElement('article');
  row.className = 'reply-backlog-row';
  row.innerHTML = `
    <div class="reply-backlog-row__name"></div>
    <div class="reply-backlog-row__stats muted"></div>
  `;

  const nameEl = row.querySelector('.reply-backlog-row__name') as HTMLDivElement;
  const statsEl = row.querySelector('.reply-backlog-row__stats') as HTMLDivElement;
  nameEl.textContent = entry.displayName;
  statsEl.textContent = [
    `${t('admin.replyBacklog.unopened')}: ${entry.unopenedCount}`,
    `${t('admin.replyBacklog.disk')}: ${formatBytes(entry.totalReplyBytes)}`,
  ].join(' · ');

  return row;
}

const GIB = 1024 ** 3;
const DAY_SECONDS = 86_400;

let passwordPoliciesDraft: PasswordPolicy[] = [];

function populatePasswordPoliciesEditor(
  editor: HTMLElement,
  settings: SystemSettingsResponse,
  options?: {
    addButton?: HTMLButtonElement | null;
    pendingHint?: HTMLElement | null;
    successHint?: HTMLElement | null;
  },
): void {
  passwordPoliciesDraft = clonePasswordPolicies(settings.passwordPolicies ?? []);
  if (options?.pendingHint) {
    options.pendingHint.hidden = true;
  }
  mountPasswordPoliciesEditor(editor, passwordPoliciesDraft, (next) => {
    passwordPoliciesDraft = next;
    if (options?.pendingHint) {
      options.pendingHint.hidden = false;
    }
    if (options?.successHint) {
      options.successHint.hidden = true;
    }
  }, { addButton: options?.addButton ?? null });
}

function populateSettingsForm(form: HTMLFormElement, settings: SystemSettingsResponse): void {
  const set = (name: string, value: string | number) => {
    const input = form.elements.namedItem(name) as HTMLInputElement | null;
    if (input) {
      input.value = String(value);
    }
  };

  set('failureWindowMinutes', settings.guestVerifyLockout.failureWindowMinutes);
  set('maxFailuresPerLink', settings.guestVerifyLockout.maxFailuresPerLink);
  set('lockoutMinutes', settings.guestVerifyLockout.lockoutMinutes);
  set('maxRequestsPerIp', settings.guestVerifyLockout.maxRequestsPerIp);
  set('maxFolderExtensionDays', settings.fileSharing.maxFolderExtensionDays);
  set('maxFolderLifetimeDays', settings.fileSharing.maxFolderLifetimeDays);
  set('fileDeleteRetentionDays', settings.fileSharing.fileDeleteRetentionDays);
  set('folderExpiryWarningDays', settings.fileSharing.folderExpiryWarningDays);
  set('folderExpiryWarningSendTimeUtc', settings.fileSharing.folderExpiryWarningSendTimeUtc);
  set(
    'incompleteDownloadFallbackDays',
    Math.round(settings.informationSharing.incompleteDownloadFallbackSeconds / DAY_SECONDS),
  );
  set('unopenedReplyRetentionDays', settings.informationSharing.unopenedReplyRetentionDays);
  set('deploymentWarningGiB', Math.round((settings.storage.deploymentWarningBytes / GIB) * 10) / 10);
  set('guestIdleTtlMinutes', settings.guestSession.idleTtlMinutes);
  set('guestMaxLifetimeHours', settings.guestSession.maxLifetimeHours);
  set('uploadSessionIdleTtlHours', settings.upload.sessionIdleTtlHours);
  const staffDomains = form.elements.namedItem('staffEmailDomains') as HTMLTextAreaElement | null;
  if (staffDomains) {
    staffDomains.value = (settings.staffEmailDomains ?? []).join('\n');
  }

  const enabled = form.elements.namedItem('cmComBudgetEnabled') as HTMLInputElement | null;
  if (enabled) {
    enabled.checked = settings.cmComBudget.enabled;
  }
  const accountId = form.elements.namedItem('cmComLogicalAccountId') as HTMLInputElement | null;
  if (accountId) {
    accountId.value = settings.cmComBudget.logicalAccountId ?? '';
  }
  set('cmComCreditValidityDays', settings.cmComBudget.creditValidityDays);
  set('cmComLowBalanceThreshold', settings.cmComBudget.lowBalanceThreshold);
  set('cmComExpiryWarningDays', settings.cmComBudget.expiryWarningDays);
  set('cmComPollSendTimeUtc', settings.cmComBudget.pollSendTimeUtc);
  const phones = form.elements.namedItem('cmComOpsPhoneNumbers') as HTMLTextAreaElement | null;
  if (phones) {
    phones.value = settings.cmComBudget.opsPhoneNumbers.join('\n');
  }
  const seed = form.elements.namedItem('cmComSeededLastTopUpDate') as HTMLInputElement | null;
  if (seed) {
    seed.value = settings.cmComBudget.seededLastTopUpDate ?? '';
  }
}

function renderSmtpStatus(settings: SystemSettingsResponse): string {
  if (settings.smtp.configured) {
    return t('admin.settings.smtp.configured', {
      host: settings.smtp.host ?? '',
      from: settings.smtp.fromAddress,
    });
  }

  if (settings.smtp.usesDevLogSink) {
    return t('admin.settings.smtp.devLog');
  }

  return t('admin.settings.smtp.notConfigured');
}

function formatOptionalDate(value: string | null): string {
  if (!value) {
    return t('admin.settings.cmComBudget.status.none');
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatOptionalDay(value: string | null): string {
  if (!value) {
    return t('admin.settings.cmComBudget.status.none');
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString().slice(0, 10);
}

function renderCmComBudgetStatus(settings: SystemSettingsResponse): string {
  const status = settings.cmComBudgetStatus;
  const smsLine = status.smsConfigured
    ? t('admin.settings.cmComBudget.status.smsConfigured')
    : status.usesDevLogSink
      ? t('admin.settings.cmComBudget.status.smsDevLog')
      : t('admin.settings.cmComBudget.status.smsNotConfigured');
  const amount =
    status.lastAmount == null
      ? t('admin.settings.cmComBudget.status.none')
      : `${status.currency ? `${status.currency} ` : ''}${status.lastAmount}`;
  const lines = [
    smsLine,
    `${t('admin.settings.cmComBudget.status.lastAmount')}: ${amount}`,
    `${t('admin.settings.cmComBudget.status.lastPolled')}: ${formatOptionalDate(status.lastPolledAt)}`,
    `${t('admin.settings.cmComBudget.status.lastTopUp')}: ${formatOptionalDate(status.lastIncreaseAt)}`,
    `${t('admin.settings.cmComBudget.status.estimatedExpiry')}: ${formatOptionalDay(status.estimatedExpiryAt)}`,
    `${t('admin.settings.cmComBudget.status.lastAlert')}: ${
      status.lastAlertKind
        ? `${status.lastAlertKind} (${formatOptionalDate(status.lastAlertedAt)})`
        : t('admin.settings.cmComBudget.status.none')
    }`,
  ];
  if (status.lastPollError) {
    lines.push(`${t('admin.settings.cmComBudget.status.lastError')}: ${status.lastPollError}`);
  }
  return lines.join('\n');
}

export function AdminPage(): HTMLElement {
  const state = getAppState();
  const roles = state.kind === 'authenticated' ? state.session.roles : [];
  const isAdmin = roles.includes('Admin');
  const canReadAudit = isAdmin || roles.includes('Auditor');

  const page = document.createElement('section');
  page.className = 'module-page module-page--admin';
  page.dataset.adminWorkspace = '';

  page.innerHTML = `
    <section class="module-section" id="admin-system-section" hidden>
      <div class="module-section__header">
        <h2 data-i18n="admin.system.title"></h2>
      </div>
      <div id="admin-system-panel">
        <p class="muted" data-i18n="status.loading"></p>
      </div>
      <p class="error-text" id="admin-system-error" hidden></p>
    </section>
    <section class="module-section" id="admin-disk-section" hidden>
      <div class="module-section__header">
        <h2 data-i18n="admin.disk.title"></h2>
      </div>
      <div class="disk-usage-panel" id="admin-disk-panel">
        <p class="muted" data-i18n="status.loading"></p>
      </div>
      <div class="disk-analytics" id="admin-disk-analytics" hidden>
        <div class="disk-analytics__exports" id="admin-disk-analytics-exports"></div>
        <div id="admin-disk-charts"></div>
        <div id="admin-disk-shared-folders"></div>
        <div id="admin-disk-sedr-rooms"></div>
        <div id="admin-disk-info-share"></div>
      </div>
      <p class="error-text" id="admin-disk-error" hidden></p>
    </section>
    <section class="module-section" id="admin-reply-backlog-section" hidden>
      <div class="module-section__header">
        <h2 data-i18n="admin.replyBacklog.title"></h2>
      </div>
      <div id="admin-reply-backlog-list" class="reply-backlog-list">
        <p class="muted" data-i18n="status.loading"></p>
      </div>
      <p class="error-text" id="admin-reply-backlog-error" hidden></p>
    </section>
    <section class="module-section" id="admin-settings-section" hidden>
      <div class="module-section__header">
        <h2 data-i18n="admin.settings.title"></h2>
      </div>
      <p class="muted" data-i18n="admin.settings.subtitle"></p>
      <form class="form-grid admin-settings-form" id="admin-settings-form">
        <fieldset class="admin-settings-group">
          <legend data-i18n="admin.settings.guestLockout.title"></legend>
          <label>
            <span data-i18n="admin.settings.guestLockout.failureWindowMinutes"></span>
            <span class="input-with-unit">
              <input type="number" name="failureWindowMinutes" class="input-compact" min="1" max="1440" required />
              <span data-i18n="admin.settings.unit.minutes"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.guestLockout.maxFailuresPerLink"></span>
            <input type="number" name="maxFailuresPerLink" class="input-compact" min="1" max="100" required />
          </label>
          <label>
            <span data-i18n="admin.settings.guestLockout.lockoutMinutes"></span>
            <span class="input-with-unit">
              <input type="number" name="lockoutMinutes" class="input-compact" min="1" max="1440" required />
              <span data-i18n="admin.settings.unit.minutes"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.guestLockout.maxRequestsPerIp"></span>
            <input type="number" name="maxRequestsPerIp" class="input-compact" min="1" max="10000" required />
          </label>
        </fieldset>
        <fieldset class="admin-settings-group">
          <legend data-i18n="admin.settings.fileSharing.title"></legend>
          <label>
            <span data-i18n="admin.settings.fileSharing.maxFolderExtensionDays"></span>
            <span class="input-with-unit">
              <input type="number" name="maxFolderExtensionDays" class="input-compact" min="1" max="365" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.fileSharing.maxFolderLifetimeDays"></span>
            <span class="input-with-unit">
              <input type="number" name="maxFolderLifetimeDays" class="input-compact" min="1" max="3650" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.fileSharing.fileDeleteRetentionDays"></span>
            <span class="input-with-unit">
              <input type="number" name="fileDeleteRetentionDays" class="input-compact" min="1" max="90" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.fileSharing.folderExpiryWarningDays"></span>
            <span class="input-with-unit">
              <input type="number" name="folderExpiryWarningDays" class="input-compact" min="1" max="90" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.fileSharing.folderExpiryWarningSendTimeUtc"></span>
            <input type="time" name="folderExpiryWarningSendTimeUtc" class="input-compact input-compact--time" required />
          </label>
        </fieldset>
        <fieldset class="admin-settings-group">
          <legend data-i18n="admin.settings.other.title"></legend>
          <label>
            <span data-i18n="admin.settings.other.incompleteDownloadFallbackDays"></span>
            <span class="input-with-unit">
              <input type="number" name="incompleteDownloadFallbackDays" class="input-compact" min="0" max="365" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.other.unopenedReplyRetentionDays"></span>
            <span class="input-with-unit">
              <input type="number" name="unopenedReplyRetentionDays" class="input-compact" min="1" max="3650" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.other.deploymentWarningGiB"></span>
            <span class="input-with-unit">
              <input type="number" name="deploymentWarningGiB" class="input-compact" min="1" max="10000" step="0.1" required />
              <span data-i18n="admin.settings.unit.gib"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.other.guestIdleTtlMinutes"></span>
            <span class="input-with-unit">
              <input type="number" name="guestIdleTtlMinutes" class="input-compact" min="5" max="1440" required />
              <span data-i18n="admin.settings.unit.minutes"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.other.guestMaxLifetimeHours"></span>
            <span class="input-with-unit">
              <input type="number" name="guestMaxLifetimeHours" class="input-compact" min="1" max="168" required />
              <span data-i18n="admin.settings.unit.hours"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.other.uploadSessionIdleTtlHours"></span>
            <span class="input-with-unit">
              <input type="number" name="uploadSessionIdleTtlHours" class="input-compact" min="1" max="168" required />
              <span data-i18n="admin.settings.unit.hours"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.other.staffEmailDomains"></span>
            <textarea name="staffEmailDomains" rows="3" class="input" spellcheck="false"></textarea>
            <span class="muted" data-i18n="admin.settings.other.staffEmailDomainsHint"></span>
          </label>
        </fieldset>
        <fieldset class="admin-settings-group">
          <legend data-i18n="admin.settings.cmComBudget.title"></legend>
          <p class="muted" data-i18n="admin.settings.cmComBudget.subtitle"></p>
          <label data-i18n="admin.settings.cmComBudget.alertsHeader"></label>
          <label class="checkbox-row">
            <input type="checkbox" name="cmComBudgetEnabled" />
            <span data-i18n="admin.settings.cmComBudget.enabled"></span>
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.logicalAccountId"></span>
            <input type="text" name="cmComLogicalAccountId" maxlength="36" autocomplete="off" />
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.creditValidityDays"></span>
            <span class="input-with-unit">
              <input type="number" name="cmComCreditValidityDays" class="input-compact" min="1" max="3650" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.lowBalanceThreshold"></span>
            <input type="number" name="cmComLowBalanceThreshold" class="input-compact" min="0" max="1000000000" step="0.01" required />
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.expiryWarningDays"></span>
            <span class="input-with-unit">
              <input type="number" name="cmComExpiryWarningDays" class="input-compact" min="1" max="90" required />
              <span data-i18n="admin.settings.unit.days"></span>
            </span>
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.pollSendTimeUtc"></span>
            <input type="time" name="cmComPollSendTimeUtc" class="input-compact input-compact--time" required />
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.opsPhoneNumbers"></span>
            <textarea name="cmComOpsPhoneNumbers" rows="3"></textarea>
          </label>
          <label>
            <span data-i18n="admin.settings.cmComBudget.seededLastTopUpDate"></span>
            <input type="date" name="cmComSeededLastTopUpDate" class="input-compact input-compact--date" />
          </label>
          <p class="muted" data-i18n="admin.settings.cmComBudget.estimateNote"></p>
          <pre class="disk-usage-panel__text" id="admin-cmcom-budget-status"></pre>
        </fieldset>
        <p class="muted" id="admin-smtp-status"></p>
        <button type="submit" class="button primary">
          <span data-i18n="admin.settings.save"></span>
        </button>
      </form>
      <p class="error-text" id="admin-settings-error" hidden></p>
      <p class="muted" id="admin-settings-success" hidden data-i18n="admin.settings.saved"></p>
    </section>
    <section class="module-section" id="admin-password-policies-section" hidden>
      <div class="module-section__header">
        <h2 data-i18n="admin.passwordPolicies.title"></h2>
        <button type="button" class="button secondary" id="admin-password-policies-add">
          <span data-i18n="admin.settings.passwordPolicies.add"></span>
        </button>
      </div>
      <p class="muted" data-i18n="admin.passwordPolicies.subtitle"></p>
      <form class="admin-password-policies-form" id="admin-password-policies-form">
        <div id="admin-password-policies-editor"></div>
        <p class="error-text" id="admin-password-policies-error" hidden></p>
        <p class="muted" id="admin-password-policies-pending" hidden data-i18n="admin.passwordPolicies.pending"></p>
        <div class="admin-password-policies-form__actions">
          <button type="submit" class="button primary" data-i18n="admin.passwordPolicies.save"></button>
        </div>
      </form>
      <p class="muted" id="admin-password-policies-success" hidden data-i18n="admin.passwordPolicies.saved"></p>
    </section>
    <section class="module-section audit-section" id="admin-audit-section">
      <div class="module-section__header">
        <h2 data-i18n="admin.audit.title"></h2>
      </div>
      <div class="audit-layout">
        <aside class="audit-filters">
          <div class="sedr-filters" id="admin-audit-modules"></div>
          <form class="form-grid audit-filters__form" id="admin-audit-filters">
            <label>
              <span data-i18n="admin.audit.filter.from"></span>
              <input type="datetime-local" name="from" />
            </label>
            <label>
              <span data-i18n="admin.audit.filter.to"></span>
              <input type="datetime-local" name="to" />
            </label>
            <label>
              <span data-i18n="admin.audit.filter.eventType"></span>
              <select name="eventType"></select>
            </label>
            <label>
              <span data-i18n="admin.audit.filter.actor"></span>
              <select name="actorUserId"></select>
            </label>
            <div class="audit-filters__actions">
              <button type="submit" class="button primary" id="admin-audit-search">
                <span data-i18n="admin.audit.search"></span>
              </button>
              <button type="button" class="button ghost" id="admin-audit-clear">
                <span data-i18n="admin.audit.clearFilters"></span>
              </button>
            </div>
          </form>
        </aside>
        <div class="audit-results">
          <div class="audit-results__header">
            <p class="muted" id="admin-audit-status"></p>
            <button type="button" class="button ghost" id="admin-audit-export">
              <span data-i18n="admin.audit.exportCsv"></span>
            </button>
          </div>
          <div id="admin-audit-list" class="audit-list"></div>
          <div class="audit-list__actions">
            <button type="button" class="button ghost" id="admin-audit-load-more" hidden>
              <span data-i18n="admin.audit.loadMore"></span>
            </button>
            <button type="button" class="button ghost" id="admin-audit-load-all" hidden>
              <span data-i18n="admin.audit.loadAll"></span>
            </button>
          </div>
          <p class="error-text" id="admin-audit-error" hidden></p>
        </div>
      </div>
    </section>
    <section class="module-section" id="admin-recovery-section" hidden>
      <h2 data-i18n="admin.recovery.title"></h2>
      <p class="muted" data-i18n="admin.recovery.subtitle"></p>
      <form class="form-grid admin-recovery-form" id="admin-recovery-form" hidden>
        <label>
          <span data-i18n="admin.recovery.user"></span>
          <select name="userId" required></select>
        </label>
        <button type="submit" class="button primary">
          <span data-i18n="admin.recovery.invalidate"></span>
        </button>
      </form>
      <p class="muted" id="admin-recovery-empty" hidden data-i18n="admin.recovery.empty"></p>
      <p class="error-text" id="admin-recovery-error" hidden></p>
      <p class="muted" id="admin-recovery-success" hidden data-i18n="admin.recovery.invalidated"></p>
    </section>
    <section class="module-section" id="admin-emergency-revoke-section" hidden>
      <div class="module-section__header">
        <h2 data-i18n="admin.emergencyRevoke.title"></h2>
      </div>
      <p class="muted" data-i18n="admin.emergencyRevoke.subtitle"></p>
      <p class="muted" data-i18n="admin.emergencyRevoke.findId"></p>
      <form class="form-grid" id="admin-emergency-revoke-form">
        <label>
          <span data-i18n="admin.emergencyRevoke.resourceType"></span>
          <select name="resourceType" required>
            <option value="info_share" data-i18n="admin.emergencyRevoke.typeInfoShare"></option>
            <option value="shared_folder" data-i18n="admin.emergencyRevoke.typeSharedFolder"></option>
            <option value="sedr_room" data-i18n="admin.emergencyRevoke.typeSedrRoom"></option>
          </select>
        </label>
        <label>
          <span data-i18n="admin.emergencyRevoke.resourceId"></span>
          <input
            type="text"
            name="resourceId"
            required
            autocomplete="off"
            spellcheck="false"
            data-i18n-placeholder="admin.emergencyRevoke.resourceIdPlaceholder"
          />
        </label>
        <button type="submit" class="button danger">
          <span data-i18n="admin.emergencyRevoke.submit"></span>
        </button>
      </form>
      <p class="error-text" id="admin-emergency-revoke-error" hidden></p>
      <p class="muted" id="admin-emergency-revoke-success" hidden></p>
    </section>
  `;

  const list = page.querySelector('#admin-audit-list') as HTMLDivElement;
  const moduleChips = page.querySelector('#admin-audit-modules') as HTMLDivElement;
  const filterForm = page.querySelector('#admin-audit-filters') as HTMLFormElement;
  const eventTypeSelect = filterForm.elements.namedItem('eventType') as HTMLSelectElement;
  const actorSelect = filterForm.elements.namedItem('actorUserId') as HTMLSelectElement;
  const fromInput = filterForm.elements.namedItem('from') as HTMLInputElement;
  const toInput = filterForm.elements.namedItem('to') as HTMLInputElement;
  const searchButton = page.querySelector('#admin-audit-search') as HTMLButtonElement;
  const loadMoreButton = page.querySelector('#admin-audit-load-more') as HTMLButtonElement;
  const loadAllButton = page.querySelector('#admin-audit-load-all') as HTMLButtonElement;
  const exportButton = page.querySelector('#admin-audit-export') as HTMLButtonElement;
  const clearFiltersButton = page.querySelector('#admin-audit-clear') as HTMLButtonElement;
  const auditStatus = page.querySelector('#admin-audit-status') as HTMLParagraphElement;
  const auditError = page.querySelector('#admin-audit-error') as HTMLParagraphElement;
  const recoverySection = page.querySelector('#admin-recovery-section') as HTMLElement;
  const recoveryForm = page.querySelector('#admin-recovery-form') as HTMLFormElement;
  const recoveryUserSelect = recoveryForm.elements.namedItem('userId') as HTMLSelectElement;
  const recoveryEmpty = page.querySelector('#admin-recovery-empty') as HTMLParagraphElement;
  const recoveryError = page.querySelector('#admin-recovery-error') as HTMLParagraphElement;
  const recoverySuccess = page.querySelector('#admin-recovery-success') as HTMLParagraphElement;
  const emergencyRevokeSection = page.querySelector('#admin-emergency-revoke-section') as HTMLElement;
  const emergencyRevokeForm = page.querySelector('#admin-emergency-revoke-form') as HTMLFormElement;
  const emergencyRevokeError = page.querySelector('#admin-emergency-revoke-error') as HTMLParagraphElement;
  const emergencyRevokeSuccess = page.querySelector('#admin-emergency-revoke-success') as HTMLParagraphElement;
  const systemSection = page.querySelector('#admin-system-section') as HTMLElement;
  const systemPanel = page.querySelector('#admin-system-panel') as HTMLDivElement;
  const systemError = page.querySelector('#admin-system-error') as HTMLParagraphElement;
  const diskSection = page.querySelector('#admin-disk-section') as HTMLElement;
  const diskPanel = page.querySelector('#admin-disk-panel') as HTMLDivElement;
  const diskAnalytics = page.querySelector('#admin-disk-analytics') as HTMLDivElement;
  const diskAnalyticsExports = page.querySelector('#admin-disk-analytics-exports') as HTMLDivElement;
  const diskCharts = page.querySelector('#admin-disk-charts') as HTMLDivElement;
  const diskSharedFolders = page.querySelector('#admin-disk-shared-folders') as HTMLDivElement;
  const diskSedrRooms = page.querySelector('#admin-disk-sedr-rooms') as HTMLDivElement;
  const diskInfoShare = page.querySelector('#admin-disk-info-share') as HTMLDivElement;
  const diskError = page.querySelector('#admin-disk-error') as HTMLParagraphElement;
  const replyBacklogSection = page.querySelector('#admin-reply-backlog-section') as HTMLElement;
  const replyBacklogList = page.querySelector('#admin-reply-backlog-list') as HTMLDivElement;
  const replyBacklogError = page.querySelector('#admin-reply-backlog-error') as HTMLParagraphElement;
  const settingsSection = page.querySelector('#admin-settings-section') as HTMLElement;
  const settingsForm = page.querySelector('#admin-settings-form') as HTMLFormElement;
  const passwordPoliciesSection = page.querySelector('#admin-password-policies-section') as HTMLElement;
  const passwordPoliciesForm = page.querySelector('#admin-password-policies-form') as HTMLFormElement;
  const passwordPoliciesEditor = page.querySelector('#admin-password-policies-editor') as HTMLElement;
  const passwordPoliciesAdd = page.querySelector('#admin-password-policies-add') as HTMLButtonElement;
  const passwordPoliciesError = page.querySelector('#admin-password-policies-error') as HTMLParagraphElement;
  const passwordPoliciesPending = page.querySelector('#admin-password-policies-pending') as HTMLParagraphElement;
  const passwordPoliciesSuccess = page.querySelector('#admin-password-policies-success') as HTMLParagraphElement;
  const opsPhoneTextarea = settingsForm.querySelector(
    'textarea[name="cmComOpsPhoneNumbers"]',
  ) as HTMLTextAreaElement | null;
  if (opsPhoneTextarea) {
    bindPhoneInputNormalize(opsPhoneTextarea);
  }
  const smtpStatus = page.querySelector('#admin-smtp-status') as HTMLParagraphElement;
  const cmComBudgetStatus = page.querySelector('#admin-cmcom-budget-status') as HTMLPreElement;
  const settingsError = page.querySelector('#admin-settings-error') as HTMLParagraphElement;
  const settingsSuccess = page.querySelector('#admin-settings-success') as HTMLParagraphElement;

  const auditSection = page.querySelector('#admin-audit-section') as HTMLElement;

  if (isAdmin) {
    recoverySection.hidden = false;
    emergencyRevokeSection.hidden = false;
    systemSection.hidden = false;
    diskSection.hidden = false;
    replyBacklogSection.hidden = false;
    settingsSection.hidden = false;
    passwordPoliciesSection.hidden = false;
  }

  const initialFilters = readAuditFiltersFromUrl();
  const urlState = readAdminUrlState();
  if (auditFiltersActive(initialFilters) && !urlState?.section) {
    history.replaceState(
      history.state,
      '',
      buildAdminUrl('audit', { search: window.location.search }),
    );
  }

  const sectionNav = mountSectionNav(
    page,
    [
      { id: 'system', labelKey: 'admin.system.title', panel: systemSection },
      { id: 'disk', labelKey: 'admin.disk.title', panel: diskSection },
      { id: 'replyBacklog', labelKey: 'admin.replyBacklog.title', panel: replyBacklogSection },
      { id: 'settings', labelKey: 'admin.settings.title', panel: settingsSection },
      { id: 'passwordPolicies', labelKey: 'admin.passwordPolicies.title', panel: passwordPoliciesSection },
      { id: 'audit', labelKey: 'admin.audit.title', panel: auditSection },
      { id: 'recovery', labelKey: 'admin.recovery.title', panel: recoverySection },
      { id: 'emergencyRevoke', labelKey: 'admin.emergencyRevoke.title', panel: emergencyRevokeSection },
    ],
    {
      navAriaLabelKey: 'nav.admin',
      initialId: auditFiltersActive(initialFilters) ? 'audit' : undefined,
      buildUrl: (id) => {
        const section = id as AdminSection;
        if (section === 'audit') {
          return buildAdminUrl('audit', { search: window.location.search });
        }
        return buildAdminUrl(section);
      },
      readId: () => readAdminUrlState()?.section ?? null,
    },
  );

  // Unavailable or unknown section: sync panel and normalize the URL.
  const pathAfterMount = window.location.pathname.replace(/\/+$/, '') || '/';
  const parsedAfterMount = parseAdminUrl(pathAfterMount);
  if (parsedAfterMount?.section && sectionNav.getActiveId() !== parsedAfterMount.section) {
    sectionNav.applyFromUrl();
  } else if (pathAfterMount.startsWith('/admin/') && !parsedAfterMount?.section) {
    const active = sectionNav.getActiveId() as AdminSection;
    history.replaceState(history.state, '', buildAdminUrl(active || null));
  }

  const onAdminRoute = () => {
    sectionNav.applyFromUrl();
  };
  page.addEventListener('admin:route', onAdminRoute);

  let events: AuditEvent[] = [];
  let hasMore = false;
  let totalCount = 0;
  let auditBusy = false;
  let auditFilters: AuditFilters = initialFilters;
  let draftModule: AuditModule = initialFilters.module;
  let directoryUsers: AdminUserSummary[] = [];
  let auditErrorKey: string | null = null;
  let diskUsage: DiskUsageResponse | null = null;
  let storageAnalytics: StorageAnalyticsResponse | null = null;
  let storageSharedFolders: StorageSharedFolderItem[] = [];
  let storageSedrRooms: StorageSedrRoomItem[] = [];
  let storageInfoShareOwners: StorageInfoShareOwner[] = [];
  let expandedSharedFolderId: string | null = null;
  let expandedSedrRoomId: string | null = null;
  let expandedInfoShareOwnerId: string | null = null;
  let sharedFolderContributors: StorageContributor[] | null = null;
  let sedrRoomContributors: StorageContributor[] | null = null;
  let systemInfo: SystemInfoResponse | null = null;
  let replyBacklog: ReplyBacklogEntry[] = [];
  let replyBacklogLoaded = false;
  let systemSettings: SystemSettingsResponse | null = null;
  let recoveryUsers: RecoveryUser[] = [];
  let recoveryUsersLoaded = false;

  function recoveryUserLabel(user: RecoveryUser): string {
    return user.email ? `${user.displayName} (${user.email})` : user.displayName;
  }

  function refreshRecoverySelect(): void {
    const selected = recoveryUserSelect.value;
    recoveryUserSelect.replaceChildren();
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.setAttribute('data-i18n', 'admin.recovery.userPlaceholder');
    recoveryUserSelect.append(placeholder);
    for (const user of recoveryUsers) {
      const option = document.createElement('option');
      option.value = user.userId;
      option.textContent = recoveryUserLabel(user);
      recoveryUserSelect.append(option);
    }
    const empty = recoveryUsers.length === 0;
    recoveryForm.hidden = !recoveryUsersLoaded || empty;
    recoveryEmpty.hidden = !recoveryUsersLoaded || !empty;
    if (!empty && recoveryUsers.some((user) => user.userId === selected)) {
      recoveryUserSelect.value = selected;
    }
    applyI18n(recoveryUserSelect);
  }

  async function loadRecoveryUsers(): Promise<void> {
    const response = await api.listRecoveryUsers();
    recoveryUsers = response.items;
    recoveryUsersLoaded = true;
    refreshRecoverySelect();
  }

  function refreshDiskPanel(): void {
    if (!diskUsage) {
      return;
    }

    diskPanel.classList.toggle('disk-usage-panel--warning', diskUsage.warningThresholdReached);
    diskPanel.innerHTML = `<pre class="disk-usage-panel__text">${renderDiskUsage(diskUsage)}</pre>`;
  }

  function refreshDiskAnalytics(): void {
    if (!storageAnalytics) {
      diskAnalytics.hidden = true;
      return;
    }

    diskAnalytics.hidden = false;
    diskAnalyticsExports.replaceChildren(
      createCsvButton('admin.disk.csv.exportModules', () =>
        downloadModulesCsv(storageAnalytics!.modules),
      ),
      createCsvButton('admin.disk.csv.exportActors', () =>
        downloadActorsCsv(storageAnalytics!.actors),
      ),
    );

    diskCharts.replaceChildren(renderAnalyticsCharts(storageAnalytics));

    diskSharedFolders.replaceChildren(
      renderStorageTableSection({
        titleKey: 'admin.disk.sharedFolders.title',
        exportLabelKey: 'admin.disk.csv.exportSharedFolders',
        onExport: () => downloadSharedFoldersCsv(storageSharedFolders),
        table: renderSharedFoldersTable(
          storageSharedFolders,
          expandedSharedFolderId,
          sharedFolderContributors,
          (folderId) => {
            void toggleSharedFolderDrilldown(folderId);
          },
        ),
      }),
    );

    diskSedrRooms.replaceChildren(
      renderStorageTableSection({
        titleKey: 'admin.disk.sedrRooms.title',
        exportLabelKey: 'admin.disk.csv.exportSedrRooms',
        onExport: () => downloadSedrRoomsCsv(storageSedrRooms),
        table: renderSedrRoomsTable(
          storageSedrRooms,
          expandedSedrRoomId,
          sedrRoomContributors,
          (roomId) => {
            void toggleSedrRoomDrilldown(roomId);
          },
        ),
      }),
    );

    diskInfoShare.replaceChildren(
      renderStorageTableSection({
        titleKey: 'admin.disk.infoShare.title',
        exportLabelKey: 'admin.disk.csv.exportInfoShare',
        onExport: () => downloadInfoShareOwnersCsv(storageInfoShareOwners),
        table: renderInfoShareOwnersTable(
          storageInfoShareOwners,
          expandedInfoShareOwnerId,
          (ownerUserId) => {
            expandedInfoShareOwnerId =
              expandedInfoShareOwnerId === ownerUserId ? null : ownerUserId;
            refreshDiskAnalytics();
          },
        ),
      }),
    );

    if (
      expandedSharedFolderId &&
      sharedFolderContributors &&
      sharedFolderContributors.length > 0
    ) {
      diskSharedFolders
        .querySelector('.disk-analytics-table-section__header')
        ?.append(
          createCsvButton('admin.disk.csv.exportContributors', () =>
            downloadContributorsCsv(
              'shared-folder',
              expandedSharedFolderId!,
              sharedFolderContributors!,
            ),
          ),
        );
    }

    if (expandedSedrRoomId && sedrRoomContributors && sedrRoomContributors.length > 0) {
      diskSedrRooms
        .querySelector('.disk-analytics-table-section__header')
        ?.append(
          createCsvButton('admin.disk.csv.exportContributors', () =>
            downloadContributorsCsv('sedr-room', expandedSedrRoomId!, sedrRoomContributors!),
          ),
        );
    }

    applyI18n(diskAnalytics);
  }

  async function toggleSharedFolderDrilldown(folderId: string): Promise<void> {
    if (expandedSharedFolderId === folderId) {
      expandedSharedFolderId = null;
      sharedFolderContributors = null;
      refreshDiskAnalytics();
      return;
    }

    expandedSharedFolderId = folderId;
    sharedFolderContributors = null;
    refreshDiskAnalytics();
    try {
      const response = await api.listStorageSharedFolderContributors(folderId);
      if (expandedSharedFolderId === folderId) {
        sharedFolderContributors = response.items;
        refreshDiskAnalytics();
      }
    } catch (cause) {
      console.error('Load shared folder contributors failed', cause);
      if (expandedSharedFolderId === folderId) {
        sharedFolderContributors = [];
        refreshDiskAnalytics();
      }
    }
  }

  async function toggleSedrRoomDrilldown(roomId: string): Promise<void> {
    if (expandedSedrRoomId === roomId) {
      expandedSedrRoomId = null;
      sedrRoomContributors = null;
      refreshDiskAnalytics();
      return;
    }

    expandedSedrRoomId = roomId;
    sedrRoomContributors = null;
    refreshDiskAnalytics();
    try {
      const response = await api.listStorageSedrRoomContributors(roomId);
      if (expandedSedrRoomId === roomId) {
        sedrRoomContributors = response.items;
        refreshDiskAnalytics();
      }
    } catch (cause) {
      console.error('Load SEDR room contributors failed', cause);
      if (expandedSedrRoomId === roomId) {
        sedrRoomContributors = [];
        refreshDiskAnalytics();
      }
    }
  }

  function refreshSystemPanel(): void {
    if (!systemInfo) {
      return;
    }

    systemPanel.innerHTML = renderSystemInfo(systemInfo);
    applyI18n(systemPanel);
  }

  function refreshReplyBacklogRows(): void {
    if (!replyBacklogLoaded) {
      return;
    }

    replyBacklogList.replaceChildren(...replyBacklog.map(renderReplyBacklogRow));
    if (replyBacklog.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'muted';
      empty.setAttribute('data-i18n', 'admin.replyBacklog.empty');
      const hint = document.createElement('p');
      hint.className = 'muted';
      hint.setAttribute('data-i18n', 'admin.replyBacklog.emptyHint');
      replyBacklogList.append(empty, hint);
      applyI18n(replyBacklogList);
    }
  }

  function refreshSettingsForm(): void {
    if (!systemSettings) {
      return;
    }

    populateSettingsForm(settingsForm, systemSettings);
    populatePasswordPoliciesEditor(passwordPoliciesEditor, systemSettings, {
      addButton: passwordPoliciesAdd,
      pendingHint: passwordPoliciesPending,
      successHint: passwordPoliciesSuccess,
    });
    smtpStatus.textContent = renderSmtpStatus(systemSettings);
    cmComBudgetStatus.textContent = renderCmComBudgetStatus(systemSettings);
  }

  function currentAuditQuery() {
    return toAuditQuery(auditFilters);
  }

  function readDraftFilters(): AuditFilters {
    return {
      module: draftModule,
      from: fromInput.value,
      to: toInput.value,
      eventType: eventTypeSelect.value,
      actorUserId: actorSelect.value,
    };
  }

  function setAuditBusy(busy: boolean): void {
    auditBusy = busy;
    searchButton.disabled = busy;
    loadMoreButton.disabled = busy;
    loadAllButton.disabled = busy;
    exportButton.disabled = busy || (events.length === 0 && totalCount === 0);
    clearFiltersButton.disabled = busy;
    filterForm.querySelectorAll('input, select').forEach((el) => {
      (el as HTMLInputElement | HTMLSelectElement).disabled = busy;
    });
    moduleChips.querySelectorAll('button').forEach((button) => {
      button.disabled = busy;
    });
  }

  function refreshAuditStatus(loading = false): void {
    if (!canReadAudit) {
      auditStatus.textContent = '';
      return;
    }
    if (loading && totalCount > 0) {
      auditStatus.textContent = t('admin.audit.status.loading', {
        loaded: events.length,
        total: totalCount,
      });
      return;
    }
    if (events.length === 0) {
      auditStatus.textContent = '';
      return;
    }
    if (events.length >= AUDIT_DOM_CAP && totalCount > events.length) {
      auditStatus.textContent = t('admin.audit.status.capped', {
        loaded: events.length,
        total: totalCount,
      });
      return;
    }
    auditStatus.textContent = t('admin.audit.status.showing', {
      loaded: events.length,
      total: totalCount,
    });
  }

  function refreshAuditRows(): void {
    list.replaceChildren(...events.map(renderAuditRow));
    if (events.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'muted';
      empty.setAttribute(
        'data-i18n',
        auditFiltersActive(auditFilters) ? 'admin.audit.emptyFiltered' : 'admin.audit.empty',
      );
      list.append(empty);
      applyI18n(empty);
    }
    loadMoreButton.hidden = !hasMore || events.length >= AUDIT_DOM_CAP;
    loadAllButton.hidden = !hasMore || events.length >= AUDIT_DOM_CAP;
    exportButton.disabled = auditBusy || totalCount === 0;
    refreshAuditStatus();
  }

  function populateEventTypeSelect(selected = eventTypeSelect.value): void {
    eventTypeSelect.replaceChildren();
    const all = document.createElement('option');
    all.value = '';
    all.setAttribute('data-i18n', 'admin.audit.filter.allEvents');
    eventTypeSelect.append(all);

    const groups =
      draftModule === 'all'
        ? AUDIT_EVENT_GROUPS
        : AUDIT_EVENT_GROUPS.filter((group) => group.module === draftModule);
    const allowed = new Set(eventTypesForModule(draftModule));
    for (const group of groups) {
      const optgroup = document.createElement('optgroup');
      optgroup.label = t(`admin.audit.module.${group.module}`);
      const seen = new Set<string>();
      for (const eventType of group.eventTypes) {
        if (seen.has(eventType)) {
          continue;
        }
        seen.add(eventType);
        const option = document.createElement('option');
        option.value = eventType;
        option.textContent = tAuditEventType(eventType);
        optgroup.append(option);
      }
      eventTypeSelect.append(optgroup);
    }
    eventTypeSelect.value = selected && allowed.has(selected) ? selected : '';
    applyI18n(eventTypeSelect);
  }

  function populateActorSelect(): void {
    const selected = auditFilters.actorUserId;
    actorSelect.replaceChildren();
    const all = document.createElement('option');
    all.value = '';
    all.setAttribute('data-i18n', 'admin.audit.filter.allActors');
    actorSelect.append(all);
    for (const user of directoryUsers) {
      const option = document.createElement('option');
      option.value = user.userId;
      option.textContent = user.displayName;
      actorSelect.append(option);
    }
    if (selected) {
      if (![...actorSelect.options].some((option) => option.value === selected)) {
        const pending = document.createElement('option');
        pending.value = selected;
        pending.textContent = selected;
        actorSelect.append(pending);
      }
      actorSelect.value = selected;
    }
    applyI18n(actorSelect);
  }

  function renderModuleChips(): void {
    moduleChips.replaceChildren();
    for (const module of AUDIT_MODULES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `sedr-filter-chip${draftModule === module ? ' sedr-filter-chip--active' : ''}`;
      button.dataset.module = module;
      button.setAttribute('data-i18n', `admin.audit.module.${module}`);
      button.addEventListener('click', () => {
        if (auditBusy || draftModule === module) {
          return;
        }
        draftModule = module;
        populateEventTypeSelect();
        renderModuleChips();
      });
      moduleChips.append(button);
    }
    applyI18n(moduleChips);
  }

  function syncFilterInputs(): void {
    draftModule = auditFilters.module;
    fromInput.value = auditFilters.from;
    toInput.value = auditFilters.to;
    populateEventTypeSelect(auditFilters.eventType);
    populateActorSelect();
    renderModuleChips();
  }

  function applyFilters(next: AuditFilters): void {
    auditFilters = next;
    writeAuditFiltersToUrl(auditFilters);
    syncFilterInputs();
    void loadAudit(false);
  }

  async function loadAudit(append: boolean): Promise<void> {
    if (!canReadAudit) {
      auditErrorKey = 'admin.audit.forbidden';
      auditError.hidden = false;
      auditError.textContent = t(auditErrorKey);
      return;
    }

    auditError.hidden = true;
    auditErrorKey = null;
    setAuditBusy(true);
    try {
      const before = append && events.length > 0 ? events[events.length - 1].createdAt : undefined;
      const response = await api.listAuditEvents({
        ...currentAuditQuery(),
        limit: AUDIT_LIST_PAGE_SIZE,
        before,
      });
      events = append ? [...events, ...response.items] : response.items;
      hasMore = response.hasMore;
      totalCount = response.totalCount;
      refreshAuditRows();
    } catch (cause) {
      console.error('Load audit events failed', cause);
      auditErrorKey = 'status.error';
      auditError.hidden = false;
      auditError.textContent = t(auditErrorKey);
    } finally {
      setAuditBusy(false);
    }
  }

  async function loadAllAudit(): Promise<void> {
    if (!canReadAudit || auditBusy) {
      return;
    }

    setAuditBusy(true);
    auditError.hidden = true;
    try {
      while (hasMore && events.length < AUDIT_DOM_CAP) {
        refreshAuditStatus(true);
        const response = await api.listAuditEvents({
          ...currentAuditQuery(),
          limit: AUDIT_LOAD_ALL_PAGE_SIZE,
          before: events[events.length - 1]?.createdAt,
        });
        events = [...events, ...response.items];
        hasMore = response.hasMore;
        totalCount = response.totalCount;
        if (events.length >= AUDIT_DOM_CAP) {
          events = events.slice(0, AUDIT_DOM_CAP);
          hasMore = totalCount > events.length;
          break;
        }
      }
      refreshAuditRows();
    } catch (cause) {
      console.error('Load all audit events failed', cause);
      auditErrorKey = 'status.error';
      auditError.hidden = false;
      auditError.textContent = t(auditErrorKey);
    } finally {
      setAuditBusy(false);
    }
  }

  async function exportAuditCsv(): Promise<void> {
    if (!canReadAudit || auditBusy || totalCount === 0) {
      return;
    }

    setAuditBusy(true);
    try {
      const collected = [...events];
      let more = hasMore;
      while (more && collected.length < AUDIT_CSV_CAP) {
        refreshAuditStatus(true);
        const response = await api.listAuditEvents({
          ...currentAuditQuery(),
          limit: AUDIT_LOAD_ALL_PAGE_SIZE,
          before: collected[collected.length - 1]?.createdAt,
        });
        collected.push(...response.items);
        more = response.hasMore;
        totalCount = response.totalCount;
      }
      const rows = collected.slice(0, AUDIT_CSV_CAP);
      downloadTextFile(
        auditCsvFileName(),
        `\uFEFF${buildAuditCsv(rows)}`,
        'text/csv;charset=utf-8',
      );
      if (totalCount > AUDIT_CSV_CAP) {
        auditStatus.textContent = t('admin.audit.exportCapped', { count: AUDIT_CSV_CAP });
      } else {
        refreshAuditStatus();
      }
    } catch (cause) {
      console.error('Export audit CSV failed', cause);
      auditErrorKey = 'status.error';
      auditError.hidden = false;
      auditError.textContent = t(auditErrorKey);
    } finally {
      setAuditBusy(false);
    }
  }

  const unbindLocale = bindLocaleRefresh(() => {
    refreshAuditRows();
    populateEventTypeSelect();
    populateActorSelect();
    renderModuleChips();
    refreshDiskPanel();
    refreshDiskAnalytics();
    refreshSystemPanel();
    refreshReplyBacklogRows();
    refreshSettingsForm();
    refreshRecoverySelect();
    if (auditErrorKey && !auditError.hidden) {
      auditError.textContent = t(auditErrorKey);
    }
    applyI18n(page);
  });

  loadMoreButton.addEventListener('click', () => {
    void loadAudit(true);
  });
  loadAllButton.addEventListener('click', () => {
    void loadAllAudit();
  });
  exportButton.addEventListener('click', () => {
    void exportAuditCsv();
  });
  filterForm.addEventListener('submit', (event) => {
    event.preventDefault();
    applyFilters(readDraftFilters());
  });
  clearFiltersButton.addEventListener('click', () => {
    applyFilters(emptyAuditFilters());
  });
  list.addEventListener('audit-filter-type', (event) => {
    const eventType = (event as CustomEvent<string>).detail;
    if (!eventType || auditBusy) {
      return;
    }
    applyFilters({ ...emptyAuditFilters(), eventType });
  });

  settingsForm.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    if (target.getAttribute('aria-invalid') === 'true') {
      target.removeAttribute('aria-invalid');
      const name = target.getAttribute('name');
      if (name) {
        settingsForm
          .querySelector(`.admin-settings-field-error[data-field="${CSS.escape(name)}"]`)
          ?.remove();
      }
    }
  });
  settingsForm.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    if (target.getAttribute('aria-invalid') === 'true') {
      target.removeAttribute('aria-invalid');
      const name = target.getAttribute('name');
      if (name) {
        settingsForm
          .querySelector(`.admin-settings-field-error[data-field="${CSS.escape(name)}"]`)
          ?.remove();
      }
    }
  });

  settingsForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      settingsError.hidden = true;
      settingsSuccess.hidden = true;
      clearSettingsFieldErrors(settingsForm);

      if (!settingsForm.reportValidity()) {
        return;
      }

      if (opsPhoneTextarea) {
        const rewritten = opsPhoneTextarea.value
          .split(/\r?\n/)
          .map((line) => {
            const trimmed = line.trim();
            if (!trimmed) {
              return line;
            }
            return normalizeToE164(trimmed) ?? line;
          })
          .join('\n');
        opsPhoneTextarea.value = rewritten;
      }

      const clientFields = validateSettingsFormClient(settingsForm);
      if (Object.keys(clientFields).length > 0) {
        applySettingsFieldErrors(settingsForm, clientFields);
        settingsError.hidden = false;
        settingsError.textContent = t('admin.settings.errors.validation_failed');
        return;
      }

      const data = new FormData(settingsForm);
      const readInt = (name: string) => Number.parseInt(String(data.get(name) ?? ''), 10);
      const readFloat = (name: string) => Number.parseFloat(String(data.get(name) ?? ''));

      try {
        systemSettings = await api.updateSystemSettings({
          guestVerifyLockout: {
            failureWindowMinutes: readInt('failureWindowMinutes'),
            maxFailuresPerLink: readInt('maxFailuresPerLink'),
            lockoutMinutes: readInt('lockoutMinutes'),
            maxRequestsPerIp: readInt('maxRequestsPerIp'),
          },
          fileSharing: {
            maxFolderExtensionDays: readInt('maxFolderExtensionDays'),
            maxFolderLifetimeDays: readInt('maxFolderLifetimeDays'),
            fileDeleteRetentionDays: readInt('fileDeleteRetentionDays'),
            folderExpiryWarningDays: readInt('folderExpiryWarningDays'),
            folderExpiryWarningSendTimeUtc: String(data.get('folderExpiryWarningSendTimeUtc') ?? ''),
          },
          informationSharing: {
            incompleteDownloadFallbackSeconds: readInt('incompleteDownloadFallbackDays') * DAY_SECONDS,
            unopenedReplyRetentionDays: readInt('unopenedReplyRetentionDays'),
          },
          storage: {
            deploymentWarningBytes: Math.round(readFloat('deploymentWarningGiB') * GIB),
          },
          guestSession: {
            idleTtlMinutes: readInt('guestIdleTtlMinutes'),
            maxLifetimeHours: readInt('guestMaxLifetimeHours'),
          },
          upload: {
            sessionIdleTtlHours: readInt('uploadSessionIdleTtlHours'),
          },
          staffEmailDomains: String(data.get('staffEmailDomains') ?? '')
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line.length > 0),
          cmComBudget: {
            enabled: data.get('cmComBudgetEnabled') === 'on',
            logicalAccountId: String(data.get('cmComLogicalAccountId') ?? '').trim(),
            creditValidityDays: readInt('cmComCreditValidityDays'),
            lowBalanceThreshold: readFloat('cmComLowBalanceThreshold'),
            expiryWarningDays: readInt('cmComExpiryWarningDays'),
            pollSendTimeUtc: String(data.get('cmComPollSendTimeUtc') ?? ''),
            opsPhoneNumbers: String(data.get('cmComOpsPhoneNumbers') ?? '')
              .split(/\r?\n/)
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
              .map((line) => normalizeToE164(line) ?? line),
            seededLastTopUpDate: String(data.get('cmComSeededLastTopUpDate') ?? '').trim(),
          },
        });
        clearSettingsFieldErrors(settingsForm);
        refreshSettingsForm();
        settingsSuccess.hidden = false;
        if (diskUsage) {
          diskUsage = await api.getDiskUsage();
          refreshDiskPanel();
        }
      } catch (cause) {
        console.error('Save system settings failed', cause);
        if (cause instanceof ApiError && cause.code === 'validation_failed' && cause.fields) {
          applySettingsFieldErrors(settingsForm, cause.fields);
          settingsError.hidden = false;
          settingsError.textContent = t('admin.settings.errors.validation_failed');
          return;
        }
        settingsError.hidden = false;
        settingsError.textContent = apiErrorMessage(cause);
      }
    })();
  });

  passwordPoliciesForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      passwordPoliciesError.hidden = true;
      passwordPoliciesSuccess.hidden = true;

      const policyFields = validatePasswordPoliciesDraft(passwordPoliciesDraft);
      if (Object.keys(policyFields).length > 0) {
        passwordPoliciesError.hidden = false;
        passwordPoliciesError.textContent = Object.values(policyFields).includes('overlapping_target')
          ? t('admin.settings.passwordPolicies.errors.overlapping')
          : t('admin.settings.errors.validation_failed');
        return;
      }

      try {
        systemSettings = await api.updateSystemSettings({
          passwordPolicies: clonePasswordPolicies(passwordPoliciesDraft),
        });
        clearPasswordPoliciesCache();
        refreshSettingsForm();
        passwordPoliciesPending.hidden = true;
        passwordPoliciesSuccess.hidden = false;
      } catch (cause) {
        console.error('Save password policies failed', cause);
        if (cause instanceof ApiError && cause.code === 'validation_failed' && cause.fields) {
          passwordPoliciesError.hidden = false;
          passwordPoliciesError.textContent = Object.values(cause.fields).includes('overlapping_target')
            ? t('admin.settings.passwordPolicies.errors.overlapping')
            : t('admin.settings.errors.validation_failed');
          return;
        }
        passwordPoliciesError.hidden = false;
        passwordPoliciesError.textContent = apiErrorMessage(cause);
      }
    })();
  });

  recoveryForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      recoveryError.hidden = true;
      recoverySuccess.hidden = true;
      const userId = String(new FormData(recoveryForm).get('userId') ?? '').trim();
      if (!userId) {
        return;
      }

      try {
        await api.invalidateUserRecovery(userId);
        recoverySuccess.hidden = false;
        recoveryForm.reset();
        await loadRecoveryUsers();
        await loadAudit(false);
      } catch (cause) {
        console.error('Invalidate recovery failed', cause);
        recoveryError.hidden = false;
        recoveryError.textContent = t('status.error');
      }
    })();
  });

  emergencyRevokeForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      emergencyRevokeError.hidden = true;
      emergencyRevokeSuccess.hidden = true;
      const data = new FormData(emergencyRevokeForm);
      const resourceType = String(data.get('resourceType') ?? '').trim() as
        | 'info_share'
        | 'shared_folder'
        | 'sedr_room';
      const resourceId = String(data.get('resourceId') ?? '').trim();
      if (!resourceId || !['info_share', 'shared_folder', 'sedr_room'].includes(resourceType)) {
        return;
      }

      const confirmed = await showConfirmDialog({
        titleKey: 'admin.emergencyRevoke.confirmTitle',
        messageKey: 'admin.emergencyRevoke.confirmMessage',
        confirmLabelKey: 'admin.emergencyRevoke.submit',
        confirmClassName: 'button danger',
        helpTopic: 'admin-emergency-revoke',
      });
      if (!confirmed) {
        return;
      }

      try {
        const result = await api.emergencyRevokeGuestSessions({ resourceType, resourceId });
        emergencyRevokeSuccess.hidden = false;
        emergencyRevokeSuccess.textContent = t('admin.emergencyRevoke.success', {
          sessions: result.sessionsDeleted,
          invites: result.invitesRevoked,
        });
        emergencyRevokeForm.reset();
        await loadAudit(false);
      } catch (cause) {
        console.error('Emergency guest revoke failed', cause);
        emergencyRevokeError.hidden = false;
        emergencyRevokeError.textContent = apiErrorMessage(cause);
      }
    })();
  });

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      unbindLocale();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  if (isAdmin) {
    void (async () => {
      try {
        await loadRecoveryUsers();
      } catch (cause) {
        console.error('Load recovery users failed', cause);
        recoveryError.hidden = false;
        recoveryError.textContent = t('status.error');
      }

      try {
        systemInfo = await api.getSystemInfo();
        refreshSystemPanel();
      } catch (cause) {
        console.error('Load system info failed', cause);
        systemError.hidden = false;
        systemError.textContent = t('status.error');
      }

      try {
        diskUsage = await api.getDiskUsage();
        refreshDiskPanel();
        const [analytics, folders, rooms, infoShare] = await Promise.all([
          api.getStorageAnalytics(),
          api.listStorageSharedFolders(),
          api.listStorageSedrRooms(),
          api.listStorageInfoShareByOwner(),
        ]);
        storageAnalytics = analytics;
        storageSharedFolders = folders.items;
        storageSedrRooms = rooms.items;
        storageInfoShareOwners = infoShare.items;
        refreshDiskAnalytics();
      } catch (cause) {
        console.error('Load disk usage failed', cause);
        diskError.hidden = false;
        diskError.textContent = t('status.error');
      }

      try {
        const response = await api.getReplyBacklog();
        replyBacklog = response.items;
        replyBacklogLoaded = true;
        refreshReplyBacklogRows();
      } catch (cause) {
        console.error('Load reply backlog failed', cause);
        replyBacklogError.hidden = false;
        replyBacklogError.textContent = t('status.error');
      }

      try {
        systemSettings = await api.getSystemSettings();
        refreshSettingsForm();
      } catch (cause) {
        console.error('Load system settings failed', cause);
        settingsError.hidden = false;
        settingsError.textContent = t('status.error');
      }
    })();
  }

  applyI18n(page);
  page.querySelector('#admin-disk-section .module-section__header h2')?.append(createHelpTip('admin-disk'));
  page.querySelector('#admin-audit-section .module-section__header h2')?.append(createHelpTip('admin-audit'));
  page.querySelector('#admin-password-policies-section .module-section__header h2')
    ?.append(createHelpTip('admin-password-policies'));
  page.querySelector('#admin-emergency-revoke-section .module-section__header h2')
    ?.append(createHelpTip('admin-emergency-revoke'));
  syncFilterInputs();
  void (async () => {
    if (canReadAudit) {
      try {
        directoryUsers = (await api.listAdminUsers()).items;
        populateActorSelect();
      } catch (cause) {
        console.error('Load admin users failed', cause);
      }
    }
    await loadAudit(false);
  })();
  return page;
}
